import { useState, useCallback, useRef } from "react";
import { recaptchaService } from "@/services/recaptchaService";
import type { RecaptchaVerificationResponse } from "@/types/recaptcha";
import { demoFormUtils } from "../pages/demo/demo.utils";
import { RecaptchaService } from "../services/recaptchaService";

interface UseRecaptchaOptions {
  action?: string;
  onSuccess?: ({
    response,
    email,
    timeToSubmit
  }: {
    response: RecaptchaVerificationResponse;
    email: string;
    timeToSubmit: number;
  }) => void;
  onError?: (error: Error) => void;
}

interface UseRecaptchaReturn {
  executeRecaptcha: (email: string) => Promise<RecaptchaVerificationResponse>;
  isLoading: boolean;
  error: string | null;
  score: number | null;
}

export const useRecaptcha = (options: UseRecaptchaOptions = {}): UseRecaptchaReturn => {
  const { action = "demo_form_submit", onSuccess, onError } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState<number | null>(null);

  const startTimeRef = useRef<number>(0);

  const executeRecaptcha = useCallback(
    async (email: string): Promise<RecaptchaVerificationResponse> => {
      setIsLoading(true);
      setError(null);
      startTimeRef.current = performance.now();

      try {
        const response = await recaptchaService.executeAndVerify({ action, email });

        setScore(response.recaptcha_v3_score);

        const timeToSubmit = performance.now() - startTimeRef.current;

        if (response.success && onSuccess) {
          onSuccess({ response, email, timeToSubmit: Math.round(timeToSubmit) });
        }

        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "reCAPTCHA execution failed";
        setError(errorMessage);

        if (onError) {
          onError(err instanceof Error ? err : new Error(errorMessage));
        }

        return RecaptchaService.createRecaptchaFailure(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [action, onSuccess, onError]
  );

  return {
    executeRecaptcha,
    isLoading,
    error,
    score
  };
};
